import type { Collection } from '@ai-platform/database';
import { type FeedParams, type FeedResponse, type Course, type Challenge, FeedFormula } from '@ai-platform/utils';

import type { PromptDto } from '../../../components/types';
import { api, ApiTags } from '../../api';
import { promptsActions } from '../../slices/prompts';
import { transformPromptDtoToModel } from '../prompt/mappers';

import {
    transformApiCoursesToFrontend,
    transformApiChallengesToFrontend,
    transformApiCollectionToFrontend,
} from './transformers';

type FeedTransformedData = {
    prompts: PromptDto[];
    collections: Collection[];
    courses: Course[];
    challenges: Challenge[];
    nextCursor: string | null;
    hasMorePrompts: boolean;
    hasMoreCourses: boolean;
    hasMoreChallenges: boolean;
};

const getCacheKey = (params: FeedParams) => {
    const { hash, formula, categories } = params;

    return [hash, formula, ...(categories ?? [])].filter(Boolean).join('_');
};

const feedApi = api.injectEndpoints({
    endpoints: build => ({
        /**
         * Получить ленту с курсами, челленджами и промптами
         * GET /api/feed
         */
        getFeed: build.query<FeedTransformedData, FeedParams>({
            query: (params = {}) => ({
                url: '/feed',
                params: {
                    ...params,
                },
            }),
            serializeQueryArgs: ({ queryArgs }) => {
                const { hash, formula, categories } = queryArgs;

                return { hash, formula, categories };
            },
            transformResponse: (response: FeedResponse) => {
                const transformedPrompts = response.prompts.map(prompt => transformPromptDtoToModel(prompt));
                const collections = response.collections.map(transformApiCollectionToFrontend);
                const courses = response.courses.map(transformApiCoursesToFrontend);
                const challenges = response.challenges.map(transformApiChallengesToFrontend);

                const result: FeedTransformedData = {
                    prompts: transformedPrompts,
                    collections,
                    courses,
                    challenges,
                    nextCursor: response.next_cursor,
                    hasMorePrompts: response.has_more_prompts,
                    hasMoreCourses: response.has_more_courses,
                    hasMoreChallenges: response.has_more_challenges,
                };

                return result;
            },
            onQueryStarted: async (params, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    const { cursor, formula } = params;

                    const cacheKey = getCacheKey(params);

                    if (!cursor) {
                        dispatch(
                            promptsActions.setFeedCache({
                                key: cacheKey,
                                cache: {
                                    promptIds: data.prompts.map(p => p.id),
                                    nextCursor: data.nextCursor,
                                    hasMorePrompts: data.hasMorePrompts,
                                    hasMoreCourses: data.hasMoreCourses,
                                    hasMoreChallenges: data.hasMoreChallenges,
                                    collections: data.collections,
                                },
                                prompts: data.prompts,
                            }),
                        );

                        if (formula === FeedFormula.MVP) {
                            // Кладем в стор курсы и челленджи только для ленты "Всё"
                            dispatch(promptsActions.setCourses(data.courses));
                            dispatch(promptsActions.setChallenges(data.challenges));
                        }
                    } else {
                        dispatch(
                            promptsActions.appendToFeedCache({
                                key: cacheKey,
                                promptIds: data.prompts.map(p => p.id),
                                nextCursor: data.nextCursor,
                                hasMorePrompts: data.hasMorePrompts,
                                prompts: data.prompts,
                            }),
                        );
                    }
                } catch {
                    // Ошибка обрабатывается RTK Query
                }
            },
            providesTags: (_, __, arg) => {
                const cacheKey = getCacheKey(arg);

                return [{ type: ApiTags.Feed, id: cacheKey }];
            },
        }),
    }),
});

export const { useGetFeedQuery } = feedApi;

export { feedApi };
